import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { cefrLevels, detectCefrLevel } from '../../data/cefrLevels';

const { FiHelpCircle, FiArrowRight, FiCheck, FiX } = FiIcons;

const CefrLevelHelper = ({ selectedLevel, onLevelChange, questionText, showAnalysis = true }) => {
  const [showHelp, setShowHelp] = useState(false);
  const [suggestedLevel, setSuggestedLevel] = useState(null);
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    if (questionText && questionText.length > 10 && showAnalysis) {
      const detected = detectCefrLevel(questionText);
      setSuggestedLevel(detected);
      
      // If no level is selected yet, auto-select the suggested one
      if (!selectedLevel) {
        onLevelChange(detected);
      }
      
      // Simple analysis
      const wordCount = questionText.split(/\s+/).length;
      const complexWordCount = questionText.split(/\s+/).filter(word => word.length > 8).length;
      
      setAnalysis({
        wordCount,
        complexWordCount,
        complexityRatio: (complexWordCount / wordCount).toFixed(2),
        avgWordLength: (questionText.length / wordCount).toFixed(1)
      });
    }
  }, [questionText, showAnalysis, onLevelChange, selectedLevel]);

  const levels = Object.entries(cefrLevels);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-gray-800 font-semibold flex items-center">
          <span className="mr-2">CEFR Language Level</span>
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <SafeIcon icon={FiHelpCircle} />
          </button>
        </h3>
        {suggestedLevel && suggestedLevel !== selectedLevel && (
          <button
            onClick={() => onLevelChange(suggestedLevel)}
            className="text-xs flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          >
            <span>Use suggested ({suggestedLevel})</span>
            <SafeIcon icon={FiArrowRight} className="ml-1" />
          </button>
        )}
      </div>

      <AnimatePresence>
        {showHelp && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 bg-blue-50 p-3 rounded-lg border border-blue-100 text-sm"
          >
            <p className="text-blue-800 mb-2">
              <strong>CEFR</strong> (Common European Framework of Reference for Languages) categorizes language proficiency into six levels:
            </p>
            <ul className="space-y-1 text-blue-700 mb-2">
              <li><strong>A1-A2:</strong> Basic users (beginners, elementary)</li>
              <li><strong>B1-B2:</strong> Independent users (intermediate, upper-intermediate)</li>
              <li><strong>C1-C2:</strong> Proficient users (advanced, mastery)</li>
            </ul>
            <p className="text-blue-800">
              Select the appropriate level for your question based on vocabulary complexity and language requirements.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-3 gap-2">
        {levels.map(([key, level]) => (
          <motion.button
            key={key}
            onClick={() => onLevelChange(key)}
            className={`p-2 rounded-lg border transition-all ${
              selectedLevel === key 
                ? `${level.bgColor} ${level.borderColor} ${level.textColor} border-2` 
                : 'border-gray-200 hover:border-gray-300 bg-gray-50'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="text-center">
              <div className="font-bold">{key}</div>
              <div className="text-xs">{level.name}</div>
            </div>
          </motion.button>
        ))}
      </div>

      {selectedLevel && cefrLevels[selectedLevel] && (
        <div className="mt-3 text-sm text-gray-600">
          <p>{cefrLevels[selectedLevel].description}</p>
          <div className="mt-2 p-2 bg-gray-50 rounded-lg">
            <p className="text-xs font-medium text-gray-700 mb-1">Typical language features:</p>
            <ul className="text-xs text-gray-600 list-disc pl-4 space-y-1">
              <li><strong>Vocabulary:</strong> {cefrLevels[selectedLevel].characteristics.vocabulary}</li>
              <li><strong>Grammar:</strong> {cefrLevels[selectedLevel].characteristics.grammar}</li>
              <li><strong>Complexity:</strong> {cefrLevels[selectedLevel].characteristics.complexity}</li>
            </ul>
          </div>
        </div>
      )}

      {analysis && showAnalysis && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-500 flex justify-between">
            <span>Words: {analysis.wordCount}</span>
            <span>Complex words: {analysis.complexWordCount} ({analysis.complexityRatio})</span>
            <span>Avg word length: {analysis.avgWordLength}</span>
          </p>
          {suggestedLevel && (
            <div className="flex items-center justify-between mt-1 text-xs">
              <span className="text-gray-500">Suggested level: </span>
              <span className={`${cefrLevels[suggestedLevel]?.textColor || 'text-gray-600'} font-semibold`}>
                {suggestedLevel} ({cefrLevels[suggestedLevel]?.name || 'Unknown'})
              </span>
              <span className="flex items-center">
                {selectedLevel === suggestedLevel ? (
                  <span className="text-green-600 flex items-center">
                    <SafeIcon icon={FiCheck} className="mr-1" />
                    <span>Matching</span>
                  </span>
                ) : (
                  <span className="text-orange-500 flex items-center">
                    <SafeIcon icon={FiX} className="mr-1" />
                    <span>Different selection</span>
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      )}

      {selectedLevel && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-700 mb-1">Example questions for {selectedLevel}:</p>
          <ul className="text-xs text-gray-600 pl-4 space-y-1">
            {cefrLevels[selectedLevel]?.examples?.map((example, index) => (
              <li key={index} className="italic">"{example}"</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CefrLevelHelper;